import { List } from "@mui/material";
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AttachmentLinkEnum, DocumentResponse } from "@akitabox/api-client";

import { api } from "../../api";
import { EmptyItem } from "./EmptyItem";
import { LoadingItem } from "./LoadingItem";
import { FileSection } from "./FileSection";
import { stylesheet } from "../../stylesheet";
import { makeUseServiceCall } from "../../hooks/useServiceCall";
import { useGlobalSnackbar } from "../../consecutive-snackbar/GlobalSnackbar";
import { AttachmentDialogTestids } from "../AttachmentDialog";
import { ActiveFilters, FilterBar, FilterKind } from "./FilterBar";

export interface FileListSectionProps {
  building: string;
  className?: string;
  onFileSelection: (file: DocumentResponse) => void;
  selectedFiles: Record<DocumentResponse["_id"], DocumentResponse>;
  entityId?: string;
  entityType: AttachmentLinkEnum;
}

/**
 * Conditionally renders the list of files in a org and building (optional)
 * @param `FileListSectionProps`
 * @returns FunctionComponent
 */
export const FileListSection: FunctionComponent<FileListSectionProps> = ({
  building,
  entityId,
  entityType,
  className,
  selectedFiles,
  onFileSelection,
}) => {
  const { simple } = useGlobalSnackbar();
  const [filters, setFilters] = useState<ActiveFilters>({
    [FilterKind.NAME]: "",
    [FilterKind.EXTENSION]: "",
    [FilterKind.TAG_CATEGORY]: [],
  });

  const {
    error,
    isLoading,
    data: docsResponse,
  } = makeUseServiceCall(api.documents.getByBuilding)(
    {
      building,
      archived: false,
      limit: 100,
      name: filters[FilterKind.NAME] || undefined,
      extension: filters[FilterKind.EXTENSION] || undefined,
    },
    {
      params: filters[FilterKind.TAG_CATEGORY].reduce(
        (obj, filter) => ({
          ...obj,
          [`tags_${filter.tagCategory.cat_name}`]:
            filter.tagCategoryValue.value_name,
        }),
        {}
      ),
    }
  );

  const shouldFetchAttachments = entityId !== undefined;
  const { isLoading: attachmentsLoading, data: attachmentsResponse } =
    makeUseServiceCall(
      api.attachments.getByBuilding,
      shouldFetchAttachments
    )({
      building,
      entityType,
      // safe to cast as string here cause we only
      // perform the api call when entityId exists
      entityId: entityId as string,
    });

  const { isLoading: tagsLoading, data: tagsResponse } = makeUseServiceCall(
    api.tags.getByBuilding
  )({ building });

  useEffect(() => {
    if (!error) return;

    let message = "Unknown network error";
    // making sure error message is not ""
    if (error.message) {
      message = error.message;
    }

    simple(message, {
      severity: "error",
      testId: AttachmentDialogTestids.FileListError,
    });
  }, [error, simple]);

  const files = useMemo(() => {
    if (!docsResponse || docsResponse.data.length === 0) {
      // no docs at all so, return nothing
      return;
    }
    if (!attachmentsResponse || attachmentsResponse.data.length === 0) {
      // no attachments to be filtered so, return all docs
      return docsResponse.data;
    }
    // filters out docs that were already attached to the entity
    return docsResponse.data.filter(
      (document) =>
        !attachmentsResponse.data.some(
          (attachment) => attachment.document._id === document._id
        )
    );
  }, [docsResponse, attachmentsResponse]);

  let content: ReactNode = <EmptyItem css={ss.centered} />;

  if (isLoading || attachmentsLoading || tagsLoading) {
    content = <LoadingItem css={ss.centered} />;
  } else if (files && files.length > 0) {
    content = files.map((file) => (
      <FileSection
        file={file}
        key={file._id}
        onFileSelection={onFileSelection}
        checked={Boolean(selectedFiles[file._id])}
      />
    ));
  }

  const handleFilterChange = (filters: ActiveFilters) => {
    setFilters(filters);
  };

  return (
    <div css={ss.fileListWrapper} className={className}>
      <FilterBar
        onFilterChange={handleFilterChange}
        activeFilters={filters}
        tagCategories={tagsResponse?.data ?? []}
      ></FilterBar>
      <List>{content}</List>
    </div>
  );
};

const ss = stylesheet({
  centered: { justifyContent: "center" },
  fileListWrapper: {
    width: "100%",
    overflowY: "auto",
  },
});
